import React, { useEffect, useState } from 'react'
import ReactSelect from 'react-select'
import AsyncCreatableSelect from 'react-select/async-creatable'
import Redux from 'app/interface/redux'
import Connect from 'app/redux/connect'
import Basic from 'app/redux/actions/pages/business/basic'
import { NestAPI } from 'app/nestapi'

interface SlackChannel {
  id: string
  name: string
}

interface SalesForceAccount {
  Id: string
  Name: string
}

interface DriveFolderOption {
  value: string
  label: string
  isNew?: boolean
}

const Integrations = (props: Redux) => {
  const { main } = props.pages.business
  const [slackChannels, setSlackChannels] = useState<SlackChannel[]>([])
  const [loadingChannels, setLoadingChannels] = useState(true)
  const [sfAccounts, setSfAccounts] = useState<SalesForceAccount[]>([])
  const [loadingAccounts, setLoadingAccounts] = useState(true)
  const [driveFolderOption, setDriveFolderOption] = useState<DriveFolderOption | null>(
    main.google_drive_folder_id
      ? { value: main.google_drive_folder_id, label: main.google_drive_folder_id }
      : null
  )

  useEffect(() => {
    NestAPI.get<SlackChannel[]>('businesses/manage/slack/channels')
      .then((channels: any) =>
        setSlackChannels(
          (channels ?? []).sort((a: SlackChannel, b: SlackChannel) => a.name.localeCompare(b.name))
        )
      )
      .catch(() => setSlackChannels([]))
      .finally(() => setLoadingChannels(false))

    NestAPI.get<SalesForceAccount[]>('salesforce/accounts')
      .then((accounts: any) =>
        setSfAccounts(
          (accounts ?? []).sort((a: SalesForceAccount, b: SalesForceAccount) =>
            a.Name.localeCompare(b.Name)
          )
        )
      )
      .catch(() => setSfAccounts([]))
      .finally(() => setLoadingAccounts(false))
  }, [])

  // Resolve folder name for the currently saved folder ID
  useEffect(() => {
    if (!main.google_drive_folder_id) {
      setDriveFolderOption(null)
      return
    }
    NestAPI.get<{ id: string; name: string } | null>(
      `businesses/manage/google-drive/folders/${main.google_drive_folder_id}`
    )
      .then((res: any) => {
        const folder = res?.data ?? res
        if (folder?.name) {
          setDriveFolderOption({ value: folder.id, label: folder.name })
        } else {
          setDriveFolderOption({
            value: main.google_drive_folder_id!,
            label: main.google_drive_folder_id!,
          })
        }
      })
      .catch(() => {
        setDriveFolderOption({
          value: main.google_drive_folder_id!,
          label: main.google_drive_folder_id!,
        })
      })
  }, [main.google_drive_folder_id])

  const selectedChannel = slackChannels.find(x => x.id === main.slack_channel_id) ?? null
  const selectedAccount = sfAccounts.find(x => x.Id === main.sf_account_id) ?? null

  const loadDriveFolders = async (inputValue: string): Promise<DriveFolderOption[]> => {
    if (!inputValue.trim()) return []
    const response = await NestAPI.get<{ id: string; name: string }[]>(
      `businesses/manage/google-drive/folders?search=${encodeURIComponent(inputValue)}`
    )
    const folders: { id: string; name: string }[] = (response as any)?.data ?? response ?? []
    return folders.map(f => ({ value: f.id, label: f.name }))
  }

  return (
    <div className="m-form m-form--fit mb-5 mx-3">
      <div className="m-form__section">
        <div className="m-form__section">
          <div className="m-form__heading">
            <h3 className="m-form__heading-title">Salesforce</h3>
          </div>
          <div className="row mt-3">
            <div className="col-md-6">
              <div className="form-group m-form__group">
                <label>Salesforce Account</label>
                <ReactSelect
                  isLoading={loadingAccounts}
                  isClearable
                  options={sfAccounts}
                  value={selectedAccount}
                  getOptionLabel={(x: SalesForceAccount) => x.Name}
                  getOptionValue={(x: SalesForceAccount) => x.Id}
                  formatOptionLabel={(x: SalesForceAccount) => (
                    <div>
                      <div>{x.Name}</div>
                      <div style={{ fontSize: '0.85em', color: '#888' }}>id: {x.Id}</div>
                    </div>
                  )}
                  onChange={(option: any) => Basic.update('sf_account_id', option?.Id ?? null)}
                />
              </div>
            </div>
          </div>
          <div className="m-form__seperator m-form__seperator--dashed mt-5 mb-3" />
        </div>

        <div className="m-form__section">
          <div className="m-form__heading">
            <h3 className="m-form__heading-title">Slack</h3>
          </div>
          <div className="row mt-3">
            <div className="col-md-6">
              <div className="form-group m-form__group">
                <label>Slack Channel</label>
                <ReactSelect
                  isLoading={loadingChannels}
                  isClearable
                  options={slackChannels}
                  value={selectedChannel}
                  getOptionLabel={(x: SlackChannel) => x.name}
                  getOptionValue={(x: SlackChannel) => x.id}
                  formatOptionLabel={(x: SlackChannel) => (
                    <div>
                      <div>{x.name}</div>
                      <div style={{ fontSize: '0.85em', color: '#888' }}>id: {x.id}</div>
                    </div>
                  )}
                  onChange={(option: any) => Basic.update('slack_channel_id', option?.id ?? null)}
                />
              </div>
            </div>
          </div>
          <div className="m-form__seperator m-form__seperator--dashed mt-5 mb-3" />
        </div>

        <div className="m-form__section">
          <div className="m-form__heading">
            <h3 className="m-form__heading-title">Google Drive (Business Folder)</h3>
          </div>
          <div className="row mt-3">
            <div className="col-md-6">
              <div className="form-group m-form__group">
                <label>Google Drive Folder</label>
                <AsyncCreatableSelect
                  isClearable
                  cacheOptions
                  loadOptions={loadDriveFolders}
                  value={driveFolderOption}
                  placeholder="Search by folder name or paste a folder ID..."
                  formatOptionLabel={(option: DriveFolderOption) => (
                    <div>
                      <div>{option.label}</div>
                      {!option.isNew && option.label !== option.value && (
                        <div style={{ fontSize: '0.85em', color: '#888' }}>id: {option.value}</div>
                      )}
                    </div>
                  )}
                  formatCreateLabel={(inputValue: string) => `Use folder ID: "${inputValue}"`}
                  onChange={(option: any) => {
                    setDriveFolderOption(option ?? null)
                    Basic.update('google_drive_folder_id', option?.value ?? null)
                  }}
                  onCreateOption={(inputValue: string) => {
                    setDriveFolderOption({ value: inputValue, label: inputValue, isNew: true })
                    Basic.update('google_drive_folder_id', inputValue)
                  }}
                />
                <span className="m-form__help">
                  Search by folder name or paste the folder ID directly. The folder ID is the last
                  part of the folder URL — e.g.{' '}
                  <code>
                    drive.google.com/drive/u/0/folders/
                    <strong>1M-2JOiMnphcb_KyaIkG-xAqE1SsunDDm</strong>
                  </code>
                </span>
              </div>
            </div>
          </div>
          <div className="m-form__seperator m-form__seperator--dashed mt-5 mb-3" />
        </div>
      </div>
    </div>
  )
}

export default Connect(Integrations)
